// Fixed Price Matrix — frontend lookup + validation helpers.
//
// This is the frontend mirror of service-flow-backend/lib/price-matrix.js.
// The backend remains server-authoritative for job creation (see POST
// /api/jobs); this file exists so the booking UI can preview the exact
// same price the backend will compute, avoiding "the total changed when
// I submitted" surprises.
//
// Any divergence between the two files is a bug — keep the shape and
// semantics in lockstep.

export const PRICING_MODELS = ['base_modifiers', 'fixed_matrix']

export const DEFAULT_PRICING_MODEL = 'base_modifiers'

export function isPricingModel(value) {
  return typeof value === 'string' && PRICING_MODELS.includes(value)
}

function toInt(value) {
  if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value)) return value
  if (typeof value === 'string' && value !== '' && /^-?\d+$/.test(value)) {
    const n = parseInt(value, 10)
    return Number.isFinite(n) ? n : null
  }
  return null
}

function isNonNegativeFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function validateAxis(axis, label) {
  if (!Array.isArray(axis) || axis.length === 0) return `${label} axis must be a non-empty array`
  const seen = new Set()
  let prev = -Infinity
  for (const raw of axis) {
    const n = toInt(raw)
    if (n === null || n < 0) return `${label} axis entries must be non-negative integers`
    if (seen.has(n)) return `${label} axis entries must be unique`
    if (n < prev) return `${label} axis must be sorted ascending`
    seen.add(n)
    prev = n
  }
  return null
}

export function validateMatrix(matrix) {
  if (matrix === null || typeof matrix !== 'object' || Array.isArray(matrix)) {
    return { ok: false, error: 'price_matrix must be an object' }
  }
  const bedErr = validateAxis(matrix.bedrooms, 'bedrooms')
  if (bedErr) return { ok: false, error: bedErr }
  const bathErr = validateAxis(matrix.bathrooms, 'bathrooms')
  if (bathErr) return { ok: false, error: bathErr }

  const bedSet = new Set(matrix.bedrooms.map(toInt))
  const bathSet = new Set(matrix.bathrooms.map(toInt))

  const prices = matrix.prices
  if (prices === null || typeof prices !== 'object' || Array.isArray(prices)) {
    return { ok: false, error: 'price_matrix.prices must be an object' }
  }

  for (const bedKey of Object.keys(prices)) {
    const bed = toInt(bedKey)
    if (bed === null || !bedSet.has(bed)) {
      return { ok: false, error: `prices has entry for bedroom "${bedKey}" not in bedrooms axis` }
    }
    const row = prices[bedKey]
    if (row === null || typeof row !== 'object' || Array.isArray(row)) {
      return { ok: false, error: `prices["${bedKey}"] must be an object` }
    }
    for (const bathKey of Object.keys(row)) {
      const bath = toInt(bathKey)
      if (bath === null || !bathSet.has(bath)) {
        return { ok: false, error: `prices["${bedKey}"] has entry for bathroom "${bathKey}" not in bathrooms axis` }
      }
      if (!isNonNegativeFiniteNumber(row[bathKey])) {
        return { ok: false, error: `prices["${bedKey}"]["${bathKey}"] must be a finite number ≥ 0` }
      }
    }
  }

  if (matrix.excludedModifierIds !== undefined) {
    if (!Array.isArray(matrix.excludedModifierIds)) {
      return { ok: false, error: 'excludedModifierIds must be an array' }
    }
    for (const id of matrix.excludedModifierIds) {
      if (id == null || (typeof id !== 'string' && typeof id !== 'number')) {
        return { ok: false, error: 'excludedModifierIds entries must be strings or numbers' }
      }
    }
  }

  return { ok: true }
}

export function excludedModifierIdSet(matrix) {
  const list = matrix && Array.isArray(matrix.excludedModifierIds) ? matrix.excludedModifierIds : []
  return new Set(list.map((id) => String(id)))
}

// lookupMatrixPrice(matrix, bedrooms, bathrooms)
//   → { price: number } on success
//   → { error: 'MATRIX_INVALID' | 'ROOM_COUNT_MISSING' | 'MATRIX_PRICE_NOT_DEFINED' }
//
// Returns the EXACT configured price for a defined (bed, bath). Missing
// combinations surface as an error — never a fallback to another combo
// or to the service's base price. This is the invariant that keeps the
// frontend preview matching the backend's server-authoritative result.
export function lookupMatrixPrice(matrix, bedroomsRaw, bathroomsRaw) {
  const bed = toInt(bedroomsRaw)
  const bath = toInt(bathroomsRaw)
  if (bed === null || bath === null) return { error: 'ROOM_COUNT_MISSING' }

  const v = validateMatrix(matrix)
  if (!v.ok) return { error: 'MATRIX_INVALID', detail: v.error }

  const row = matrix.prices[String(bed)]
  if (!row) return { error: 'MATRIX_PRICE_NOT_DEFINED' }
  const price = row[String(bath)]
  if (!isNonNegativeFiniteNumber(price)) return { error: 'MATRIX_PRICE_NOT_DEFINED' }

  return { price }
}

// Build an empty matrix for a given axis spec. Used by the matrix editor
// when the user first switches to fixed_matrix mode.
export function emptyMatrix(bedrooms = [1, 2, 3, 4], bathrooms = [1, 2, 3]) {
  return {
    version: 1,
    bedrooms: [...bedrooms],
    bathrooms: [...bathrooms],
    prices: {},
    excludedModifierIds: [],
  }
}
